import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import type { SectionKey } from '@/features/dashboard/sections'

const STORAGE_KEY = 'zynx_selected_section'
const VALID_SECTIONS: SectionKey[] = ['company', 'sales', 'accounts', 'projects', 'people', 'hr']

interface SectionContextValue {
  selectedSection: SectionKey | null
  setSelectedSection: (section: SectionKey | null) => void
}

const SectionContext = createContext<SectionContextValue | undefined>(undefined)

export function SectionProvider({ children }: { children: ReactNode }) {
  const [selectedSection, setSelectedSectionState] = useState<SectionKey | null>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY)
      if (saved && VALID_SECTIONS.includes(saved as SectionKey)) {
        return saved as SectionKey
      }
    } catch {}
    return null
  })

  const setSelectedSection = useCallback((section: SectionKey | null) => {
    setSelectedSectionState(section)
    try {
      if (section) {
        sessionStorage.setItem(STORAGE_KEY, section)
        localStorage.setItem(STORAGE_KEY, section)
      } else {
        sessionStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch {}
  }, [])

  return (
    <SectionContext.Provider value={{ selectedSection, setSelectedSection }}>
      {children}
    </SectionContext.Provider>
  )
}

export function useSection() {
  const context = useContext(SectionContext)
  if (!context) {
    throw new Error('useSection must be used within a SectionProvider')
  }
  return context
}